import { Tabs } from "radix-ui";
import styles from "./segmentedToggle.css";

const SegmentedToggleList = ({ tabs, ariaLabel }) => {
  return (
    <Tabs.List className={styles.tabsList} aria-label={ariaLabel}>
      {tabs?.map((tab) => (
        <Tabs.Trigger
          key={tab.value}
          className={styles.tabsTrigger}
          value={tab.value}
        >
          {tab.label}
        </Tabs.Trigger>
      ))}
    </Tabs.List>
  );
};

/**
 * SegmentedToggle represents is a styled version of the Radix UI tabs component.
 * We use it for accessibility-friendly tab navigation.
 * With SegmentedToggleList we render a dynamic list of given tabs.
 * With SegmentedToggle we piece together the SegmentedToggleList and the content we want to display for each tab.
 *
 * @param {Object} props - The properties object.
 * @param {Array} props.tabs - An array of tab objects where each tab defines a value
 * and associated content. Each object in the array should have the following properties:
 *   - {string} value - The unique identifier for the tab.
 *   - {string} label - The label to display for the tab.
 *   - {ReactNode} content - The content to display when the tab is active.
 * @param {string} [props.value] - The current selected value of the toggle.
 * @param {Function} [props.onValueChange] - Callback function fired when the selected
 * value changes. Receives the new value as an argument.
 * @param {string} [props.defaultValue] - The initial value to be selected.
 * @param {ReactNode} [props.children] - Any additional elements to be rendered alongside
 * the tabs. For example, a header like in InviteOverview.
 *
 * @returns {ReactElement} The rendered SegmentedToggle component.
 */

const SegmentedToggle = ({
  tabs,
  value,
  onValueChange,
  defaultValue,
  children,
}) => {
  return (
    <Tabs.Root
      value={value}
      onValueChange={onValueChange}
      defaultValue={defaultValue || tabs?.[0]?.value}
      className={styles.tabsRoot}
    >
      {children}
      {tabs?.map((tab) => (
        <Tabs.Content
          key={tab.value}
          value={tab.value}
          className={styles.tabsContent}
        >
          {tab.content}
        </Tabs.Content>
      ))}
    </Tabs.Root>
  );
};

SegmentedToggle.List = SegmentedToggleList;

export default SegmentedToggle;
